import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { getAuth } from "../../modules/auth";

// Espelho das respostas de GET /plan-limits/usage e /plan-limits/plans do gateway do clube (AAC-81).
export type PlanLimitResource = "users" | "athletes" | "sponsors" | "notifications";

export const PLAN_LIMIT_RESOURCES: PlanLimitResource[] = [
  "users",
  "athletes",
  "sponsors",
  "notifications",
];

export const RESOURCE_LABELS: Record<PlanLimitResource, string> = {
  users: "usuários",
  athletes: "atletas",
  sponsors: "patrocinadores",
  notifications: "notificações por mês",
};

export interface PlanResourceUsage {
  limit: number;
  used: number;
  percent: number;
}

export interface PlanUsage {
  contract: null | {
    _id: number;
    _id_plano: number | null;
    plan_name: string | null;
    plan_amount: number | null;
    start_date: string;
    final_date: string | null;
    indefinite_term: boolean;
  };
  reason: null | "no_active_contract";
  resources: Record<PlanLimitResource, PlanResourceUsage>;
}

export interface UpgradePlan {
  _id: number;
  name: string;
  description: string | null;
  amount: number;
  limits: Record<PlanLimitResource, number>;
}

export interface UpgradePlansResponse {
  current_plan_id: number | null;
  plans: UpgradePlan[];
}

export const WARNING_PERCENT = 90;
const CACHE_TTL_MS = 60_000;

// Cache em módulo: o banner roda em toda montagem do layout e não pode chamar a rota a cada página.
// Guardado por token: o "Sair" não recarrega a aba, e quem entra em seguida (outro clube) não pode
// ver o uso do anterior.
let cache: { at: number; token: string; data: PlanUsage } | null = null;
let emVoo: { token: string; pedido: Promise<PlanUsage> } | null = null;

function currentToken(): string {
  return getAuth()?.api_token ?? "";
}

function authHeaders(token = currentToken()) {
  return { Authorization: `Bearer ${token}` };
}

export function invalidatePlanUsage(): void {
  cache = null;
  emVoo = null;
}

export function fetchPlanUsage(force = false): Promise<PlanUsage> {
  const token = currentToken();
  if (
    !force &&
    cache &&
    cache.token === token &&
    Date.now() - cache.at < CACHE_TTL_MS
  ) {
    return Promise.resolve(cache.data);
  }
  if (!force && emVoo && emVoo.token === token) return emVoo.pedido;
  const pedido: Promise<PlanUsage> = axios
    .get<PlanUsage>(`${process.env.REACT_APP_API_URL}/plan-limits/usage`, {
      headers: authHeaders(token),
    })
    .then((response) => {
      if (emVoo?.pedido === pedido) {
        cache = { at: Date.now(), token, data: response.data };
        emVoo = null;
      }
      return response.data;
    })
    .catch((error) => {
      if (emVoo?.pedido === pedido) emVoo = null;
      throw error;
    });
  emVoo = { token, pedido };
  return pedido;
}

export async function fetchUpgradePlans(): Promise<UpgradePlansResponse> {
  const response = await axios.get<UpgradePlansResponse>(
    `${process.env.REACT_APP_API_URL}/plan-limits/plans`,
    { headers: authHeaders() },
  );
  return response.data;
}

export function resourcesAtWarning(
  usage: PlanUsage,
): Array<{ resource: PlanLimitResource; usage: PlanResourceUsage }> {
  return PLAN_LIMIT_RESOURCES.map(
    (resource) => [resource, usage.resources?.[resource]] as const,
  )
    // Razão exata, não o `percent` arredondado: 449/500 (89,8%) ainda não avisa.
    .filter(([, u]) => u && u.limit > 0 && u.used * 100 >= u.limit * WARNING_PERCENT)
    .map(([resource, u]) => ({ resource, usage: u as PlanResourceUsage }));
}

// `refreshKey` faz o hook reler o cache (o banner passa a rota: navegar relê, mas só vai à rede depois
// de 60 s ou de um invalidatePlanUsage()). `force` ignora o cache (tela "Meu plano").
export function usePlanUsage(
  options: { force?: boolean; refreshKey?: string } = {},
): {
  usage: PlanUsage | null;
  loading: boolean;
  error: boolean;
  reload: () => void;
} {
  const { force = false, refreshKey } = options;
  const [usage, setUsage] = useState<PlanUsage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [tentativa, setTentativa] = useState(0);

  useEffect(() => {
    let ativo = true;
    setLoading(true);
    fetchPlanUsage(force || tentativa > 0)
      .then((data) => {
        if (!ativo) return;
        setUsage(data);
        setError(false);
      })
      .catch(() => {
        if (!ativo) return;
        setUsage(null);
        setError(true);
      })
      .finally(() => {
        if (ativo) setLoading(false);
      });
    return () => {
      ativo = false;
    };
  }, [force, tentativa, refreshKey]);

  const reload = useCallback(() => setTentativa((n) => n + 1), []);
  return { usage, loading, error, reload };
}
